import * as React from 'react';

import { TCollapsibleProps } from './types';

const CollapsibleContext = React.createContext<{
  open: boolean;
  toggle: () => void;
  // eslint-disable-next-line @typescript-eslint/no-empty-function
}>({ open: false, toggle: () => {} });

const Collapsible = ({ open = false, onOpenChange, children }: TCollapsibleProps) => {
  const [internalOpen, setInternalOpen] = React.useState(open);

  React.useEffect(() => {
    setInternalOpen(open);
  }, [open]);

  const toggle = () => {
    const newOpen = !internalOpen;
    setInternalOpen(newOpen);
    onOpenChange?.(newOpen);
  };

  return (
    <CollapsibleContext.Provider value={{ open: internalOpen, toggle }}>
      {children}
    </CollapsibleContext.Provider>
  );
};

interface CollapsibleTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;
}

const CollapsibleTrigger = React.forwardRef<HTMLButtonElement, CollapsibleTriggerProps>(
  ({ children, className, ...props }, ref) => {
    const { toggle } = React.useContext(CollapsibleContext);

    return (
      <button ref={ref} onClick={toggle} className={className} {...props}>
        {children}
      </button>
    );
  },
);
CollapsibleTrigger.displayName = 'CollapsibleTrigger';

interface CollapsibleContentProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

const CollapsibleContent = React.forwardRef<HTMLDivElement, CollapsibleContentProps>(
  ({ children, className = '', ...props }, ref) => {
    const { open } = React.useContext(CollapsibleContext);
    const contentRef = React.useRef<HTMLDivElement>(null);
    const [height, setHeight] = React.useState<number | undefined>(open ? undefined : 100);

    React.useEffect(() => {
      if (!contentRef.current) return;

      if (open) {
        const contentHeight = contentRef.current.scrollHeight;
        setHeight(contentHeight);

        const timer = setTimeout(() => setHeight(undefined), 300);
        return () => clearTimeout(timer);
      } else {
        const contentHeight = contentRef.current.scrollHeight;
        setHeight(contentHeight);
        requestAnimationFrame(() => {
          setHeight(100);
        });
      }
    }, [open]);

    return (
      <div className={`relative ${className}`} ref={ref} {...props}>
        {/* Content wrapper (clips the content height) */}
        <div
          style={{
            height: height !== undefined ? `${height}px` : 'auto',
            overflow: 'hidden',
            transition: 'height 300ms ease-in-out',
          }}
        >
          <div ref={contentRef}>{children}</div>
        </div>

        {/* Fade overlay stays OUTSIDE the clipped area */}
        {!open && (
          <div className="pointer-events-none absolute bottom-0 left-0 w-full h-10 bg-gradient-to-t from-white to-transparent" />
        )}
      </div>
    );
  },
);

CollapsibleContent.displayName = 'CollapsibleContent';

export { Collapsible, CollapsibleContent, CollapsibleTrigger };
